import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, RotateCcw, Sparkles, GraduationCap, Globe } from "lucide-react";
import { Reveal, FadeIn, MaskedLines, EASE } from "../components/Motion";
import { Button } from "../components/ui/button";

const QUESTIONS = [
  { q: "She ___ to school every day.", options: ["goes", "go", "going", "gone"], a: 0 },
  { q: "What is the past tense of 'teach'?", options: ["Teached", "Taught", "Teaching", "Teach"], a: 1 },
  { q: "I have lived in Mandya ___ 2019.", options: ["for", "since", "from", "by"], a: 1 },
  { q: "What is the plural of 'child'?", options: ["Childs", "Childes", "Children", "Childrens"], a: 2 },
  { q: "Neither of the boys ___ present yesterday.", options: ["were", "are", "was", "be"], a: 2 },
  { q: "Choose the correctly spelled word.", options: ["Recieve", "Receive", "Riceive", "Receeve"], a: 1 },
  { q: "He is very good ___ mathematics.", options: ["in", "on", "at", "for"], a: 2 },
  { q: "What is the opposite of 'ancient'?", options: ["Old", "Antique", "Historic", "Modern"], a: 3 },
  { q: "If it rains tomorrow, we ___ at home.", options: ["will stay", "would stayed", "staying", "stays"], a: 0 },
  { q: "This play was written ___ Shakespeare.", options: ["from", "by", "with", "of"], a: 1 },
  { q: "He is ___ honest man.", options: ["a", "an", "the", "no article"], a: 1 },
  { q: "She said that she ___ tired.", options: ["is", "be", "were", "was"], a: 3 },
  { q: "Choose the synonym of 'commence'.", options: ["Begin", "Finish", "Delay", "Cancel"], a: 0 },
  { q: "They ___ the match convincingly yesterday.", options: ["win", "won", "wins", "winning"], a: 1 },
  { q: "You are coming to the class, ___?", options: ["aren't you", "are you", "isn't it", "won't you"], a: 0 },
];

const LEVELS = [
  { min: 0, name: "Foundation", desc: "Perfect starting point — our Spoken English foundation batch will build you up step by step, with zero judgment.", icon: Sparkles },
  { min: 6, name: "Intermediate", desc: "Solid base! Our grammar + conversation track will polish your fluency for interviews and classrooms.", icon: GraduationCap },
  { min: 11, name: "Advanced", desc: "Impressive! You're ready for IELTS coaching — let's convert this into a band score that opens borders.", icon: Globe },
];

export default function Quiz() {
  const [stage, setStage] = useState("intro"); // intro | playing | done
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [picked, setPicked] = useState(null);

  const answer = (i) => {
    if (picked !== null) return;
    setPicked(i);
    const correct = i === QUESTIONS[idx].a;
    if (correct) setScore((s) => s + 1);
    setTimeout(() => {
      setPicked(null);
      if (idx + 1 < QUESTIONS.length) setIdx(idx + 1);
      else setStage("done");
    }, 650);
  };

  const reset = () => {
    setStage("intro");
    setIdx(0);
    setScore(0);
    setPicked(null);
  };

  const level = [...LEVELS].reverse().find((l) => score >= l.min);
  const q = QUESTIONS[idx];

  return (
    <div data-testid="quiz-page" className="px-4 pb-24 pt-32 sm:px-6 lg:pt-40">
      <div className="mx-auto max-w-2xl">
        {stage === "intro" && (
          <>
            <FadeIn>
              <p className="text-center font-mono text-xs uppercase tracking-[0.25em] text-terracotta-dark">Free English Level Test</p>
            </FadeIn>
            <h1 className="mt-4 text-center font-serif text-4xl font-bold leading-[1.08] tracking-tight text-forest-deep sm:text-5xl">
              <MaskedLines lines={["How strong is", <span key="l2" className="italic text-terracotta">your English?</span>]} />
            </h1>
            <FadeIn delay={0.5}>
              <p className="mx-auto mt-6 max-w-md text-center text-base leading-relaxed text-stone-600">
                15 quick questions, about 2 minutes. Get your level instantly — and the right course to grow it.
              </p>
              <div className="mt-8 flex justify-center">
                <Button onClick={() => setStage("playing")} size="lg" data-testid="quiz-start-button" className="rounded-full bg-forest px-10 font-display text-cream hover:bg-forest-deep">
                  Start the Test <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
              <p className="mt-4 text-center text-xs text-stone-400">No sign-up. No marks against you. Just honest feedback.</p>
            </FadeIn>
          </>
        )}

        {stage === "playing" && (
          <div data-testid="quiz-playing">
            <div className="flex items-center justify-between">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-stone-500" data-testid="quiz-progress-text">
                Question {idx + 1} of {QUESTIONS.length}
              </p>
              <p className="font-mono text-xs text-stone-400">Score: {score}</p>
            </div>
            <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-linen">
              <motion.div
                className="h-full bg-terracotta"
                animate={{ width: `${((idx + 1) / QUESTIONS.length) * 100}%` }}
                transition={{ duration: 0.5, ease: EASE }}
                data-testid="quiz-progress-bar"
              />
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.35, ease: EASE }}
                className="mt-8 rounded-3xl border border-forest/10 bg-white p-8 sm:p-10"
                data-testid="quiz-question-card"
              >
                <h2 className="font-serif text-2xl font-semibold leading-snug text-forest-deep sm:text-3xl">{q.q}</h2>
                <div className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {q.options.map((opt, i) => {
                    const isPicked = picked === i;
                    const isRight = picked !== null && i === q.a;
                    const isWrong = isPicked && i !== q.a;
                    return (
                      <button
                        key={opt}
                        data-testid={`quiz-option-${i}`}
                        onClick={() => answer(i)}
                        className={`rounded-2xl border px-5 py-4 text-left font-display text-sm font-medium transition-all duration-300 ${
                          isRight
                            ? "border-forest bg-forest text-cream"
                            : isWrong
                              ? "border-red-400 bg-red-50 text-red-700"
                              : "border-forest/15 bg-cream text-forest-deep hover:border-terracotta hover:bg-white"
                        }`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        )}

        {stage === "done" && (
          <Reveal>
            <div className="rounded-[2rem] border border-forest/10 bg-white p-10 text-center" data-testid="quiz-result">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-terracotta-dark">Your Result</p>
              <p className="mt-5 font-serif text-7xl font-bold text-forest-deep" data-testid="quiz-score">
                {score}<span className="text-3xl text-stone-400">/{QUESTIONS.length}</span>
              </p>
              <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-forest px-6 py-2.5 text-cream" data-testid="quiz-level">
                <level.icon className="h-4 w-4 text-gold" />
                <span className="font-display text-sm font-semibold">{level.name} Level</span>
              </div>
              <p className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-stone-600">{level.desc}</p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Button asChild className="rounded-full bg-terracotta px-8 font-display text-white hover:bg-terracotta-dark" data-testid="quiz-enquire-button">
                  <Link to="/contact">Enquire About the Right Course</Link>
                </Button>
                <Button variant="outline" onClick={reset} className="rounded-full border-forest/30 px-8 font-display text-forest hover:bg-forest hover:text-cream" data-testid="quiz-retry-button">
                  <RotateCcw className="mr-2 h-4 w-4" /> Retake
                </Button>
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </div>
  );
}
